// ProfileCompletionGate.js
//
// Fuerza a un usuario NUEVO a completar su info básica profesional
// (nombre, profesión, N° de registro + certificado de la Superintendencia de
// Salud) ANTES de poder operar la app. Reutiliza el VerificationWizard ya
// existente (/perfil/verificacion) y el estado `verification_status` que ya
// viene en /api/user/me/ — no requiere backend nuevo.
//
// Criterio de "perfil incompleto" (conservador, bajo riesgo):
//   - Logueado, NO superuser (los admins nunca quedan atrapados).
//   - has_access === false → todavía no fue habilitado por un admin, o sea
//     es una cuenta recién creada que aún no arrancó el onboarding. Esto
//     PROTEGE a las cuentas demo / manualmente habilitadas (has_access=true),
//     que no se ven forzadas al wizard.
//   - verification_status === "NOT_SUBMITTED" → nunca completó la info básica.
//     Apenas envía el wizard pasa a PENDING y deja de forzarse.
//
// Como estos usuarios YA no pueden usar ningún módulo clínico (ProtectedRoute
// los manda a /perfil cuando !has_access), este gate NO agrega bloqueo nuevo:
// solo cambia su landing a la pantalla donde completan su verificación, en vez
// de dejarlos sueltos en el Home.
//
// Decisiones de producto que quedan como knob (ver deliverable):
//   - Si forzar también a REJECTED (reenviar) y/o a las cuentas con has_access.
//   - Si bloquear 100% o permitir explorar el directorio.

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../context/UserContext";
import { isVerificationSkipped } from "../../helpers/verification-skip";

// Rutas donde NO forzamos el wizard, para que el usuario pueda completar su
// perfil, leer términos, autenticarse o cerrar sesión sin quedar atrapado.
const ALLOWED_WHILE_INCOMPLETE = [
  // /bienvenida ya no es una pantalla: desde el 09-sep-2026 la bienvenida es el
  // paso 0 del propio wizard y esta ruta solo redirige ahí. Se deja permitida
  // igual para que el redirect ocurra una sola vez: sin esto, el gate empuja
  // primero y la redirección después, dos navegaciones para el mismo destino.
  "/bienvenida",
  "/perfil/verificacion",
  // #1599: la pantalla de lo que falta de Quién eres. Sin esto, una cuenta `has_access=false`
  // rebotaría entre las dos pantallas.
  "/perfil/completar",
  "/perfil",
  "/perfil/editar",
  "/terms-and-conditions",
  // #1680: enlace de la casilla del checkout fuera de Chile; público, igual que los Términos.
  "/terminos-internacionales",
  "/authentication",
  // /waiting-for-access NO va acá a propósito: es la sala de espera de quien
  // YA subió todo (PENDING). Con esta ruta permitida, el que todavía no había
  // subido nada quedaba retenido ahí sin un solo botón (revisor de #878).
];

const isBasicProfileIncomplete = (user) => {
  if (!user) return false;
  if (user.is_superuser) return false; // admins nunca bloqueados
  if (user.has_access) return false; // demo / habilitados manualmente: no forzar
  // Eligió "Completar después" en el wizard: durante la demo no lo frenamos con
  // el carnet / certificado / N° de registro. Post-demo, para la venta oficial,
  // los tres se piden sí o sí (ahí hay que dejar de mirar este flag).
  if (isVerificationSkipped(user)) return false;
  const status = user.professional_profile?.verification_status;
  return status === "NOT_SUBMITTED";
};

const ProfileCompletionGate = () => {
  const { currentUser, loading } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isBasicProfileIncomplete(currentUser)) return;

    const path = location.pathname;
    const allowed = ALLOWED_WHILE_INCOMPLETE.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (!allowed) {
      navigate("/perfil/verificacion", { replace: true });
    }
  }, [currentUser, loading, location.pathname, navigate]);

  return null;
};

export default ProfileCompletionGate;
